import { useQuery } from '@tanstack/react-query';
import api from '../api/client';

export interface Activity {
  id: string;
  family_id: string;
  user_id: string;
  user_name: string;
  user_avatar: string | null;
  activity_type: string;
  title: string;
  description: string | null;
  entity_type: string;
  entity_id: string;
  entity_name: string | null;
  navigation_url: string;
  metadata: Record<string, any>;
  created_at: string;
}

interface ActivityListResponse {
  activities: Activity[];
  total: number;
  has_more: boolean;
}

export function useMyActivities(limit: number = 20) {
  return useQuery({
    queryKey: ['my-activities', limit],
    queryFn: async () => {
      const response = await api.get<ActivityListResponse>('/activities/my-activities', {
        params: { limit }
      });
      return response.data;
    },
    refetchInterval: 30000, // Refetch every 30 seconds
    staleTime: 10000, // Consider data stale after 10 seconds
  });
}

export function useFamilyActivities(familyId: string | undefined, limit: number = 20) {
  return useQuery({
    queryKey: ['family-activities', familyId, limit],
    queryFn: async () => {
      if (!familyId) return { activities: [], total: 0, has_more: false };
      const response = await api.get<ActivityListResponse>(`/activities/family/${familyId}`, {
        params: { limit }
      });
      return response.data;
    },
    enabled: !!familyId,
  });
}

// Activity type icon mapping
export function getActivityIcon(activityType: string): string {
  const iconMap: Record<string, string> = {
    document_uploaded: '📤',
    document_updated: '📝',
    document_deleted: '🗑️',
    shopping_list_created: '🛒',
    shopping_list_updated: '🛒',
    shopping_list_completed: '✅',
    shopping_item_added: '➕',
    shopping_item_checked: '☑️',
    password_created: '🔐',
    password_updated: '🔑',
    password_deleted: '🗑️',
    todo_board_created: '📋',
    todo_item_created: '✏️',
    todo_item_completed: '✅',
    todo_item_assigned: '👤',
    calendar_event_created: '📅',
    calendar_event_updated: '📅',
    gift_list_created: '🎁',
    gift_added: '🎀',
    gift_reserved: '🛍️',
    gift_purchased: '💝',
    family_created: '👨‍👩‍👧‍👦',
    member_joined: '🎉',
    member_left: '👋',
  };
  // Handle both uppercase (legacy) and lowercase activity types
  const normalizedType = activityType.toLowerCase();
  return iconMap[normalizedType] || '📌';
}

// Activity type color mapping
export function getActivityColor(activityType: string): string {
  // Handle both uppercase (legacy) and lowercase activity types
  const normalizedType = activityType.toLowerCase();
  if (normalizedType.includes('document')) return 'from-purple-500 to-pink-500';
  if (normalizedType.includes('shopping')) return 'from-emerald-500 to-teal-500';
  if (normalizedType.includes('password')) return 'from-amber-500 to-orange-500';
  if (normalizedType.includes('todo')) return 'from-blue-500 to-cyan-500';
  if (normalizedType.includes('calendar')) return 'from-rose-500 to-red-500';
  if (normalizedType.includes('gift')) return 'from-pink-500 to-rose-500';
  if (normalizedType.includes('family') || normalizedType.includes('member')) return 'from-indigo-500 to-violet-500';
  return 'from-gray-500 to-gray-600';
}

